import { useMemo } from "react";
import { Alert, Container } from "@simplybusiness/mobius";
import {
  type QuestionnaireProps,
  Questionnaire,
} from "../../components/Questionnaire";
import {
  getReactInterventionComponent,
  InterventionWrapper,
} from "@simplybusiness/mobius-interventions";
import {
  getInsuranceAdvisorData,
  InsuranceAdvisorProvider,
} from "../../contexts";
import { getPropsByName } from "../../utils";
import { Page, PageProviders } from "../Page";
import { useSetQuestionnaireFormUrl } from "../../hooks/useQuestionnaireFormUrl";
import type {
  AlertProps,
  PageProps,
  WithMetadata,
  Action,
} from "../Page/types";
import type { QuestionnairePageProps } from "./types";
import { useQuestionValidationTracking } from "../../features/questionnaire/hooks/useQuestionValidationTracking";

const QuestionnairePageInner = (props: QuestionnairePageProps) => {
  const {
    layout: { components },
  } = props;

  useQuestionValidationTracking();

  // Split state into individual components
  const alert = getPropsByName({ name: "alert", components });
  const questionnaire = getPropsByName({
    name: "questionnaire",
    components,
  }) as WithMetadata<QuestionnaireProps>;

  const submitAction = useMemo(
    () =>
      questionnaire.actions?.find(
        (action: Action) => action.actionType === "submit",
      ),
    [questionnaire.actions],
  );
  useSetQuestionnaireFormUrl(submitAction?.url);

  const { interventions = [], ...questionnaireProps } = questionnaire;

  return (
    <Container size="sm">
      {interventions.map((intervention: string) => {
        const interventionElement = getReactInterventionComponent(intervention);
        if (interventionElement) {
          return (
            <InterventionWrapper key={intervention} name={intervention}>
              {interventionElement}
            </InterventionWrapper>
          );
        }
        return null;
      })}
      {!!alert &&
        (() => {
          const { type, header, textContent } =
            alert as WithMetadata<AlertProps>;
          return (
            <Alert variant={type} header={header}>
              {textContent}
            </Alert>
          );
        })()}
      {!!questionnaire && <Questionnaire {...questionnaireProps} />}
    </Container>
  );
};

export const QuestionnairePage = (props: QuestionnairePageProps) => {
  const insuranceAdvisorData = getInsuranceAdvisorData(props as PageProps);

  return (
    <PageProviders {...(props as PageProps)}>
      <InsuranceAdvisorProvider insuranceAdvisorData={insuranceAdvisorData}>
        <Page {...(props as PageProps)}>
          <main className="mobius mobius-page__main">
            <QuestionnairePageInner {...props} />
          </main>
        </Page>
      </InsuranceAdvisorProvider>
    </PageProviders>
  );
};
